import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Caminhos das imagens (coloque suas imagens reais em public/images/educational/)
const backgroundImg = '/images/educational/manguezal-bg.jpg';
const trashImages = [
  { id: 1, src: '/images/educational/banana.png', alt: 'Casca de banana', left: '10%', top: '75%' },
  { id: 2, src: '/images/educational/resto-comida.png', alt: 'Restos de comida', left: '30%', top: '78%' },
  { id: 3, src: '/images/educational/garrafa.png', alt: 'Garrafa plástica', left: '50%', top: '80%' },
  { id: 4, src: '/images/educational/melancia.png', alt: 'Casca de melancia', left: '70%', top: '77%' },
  { id: 5, src: '/images/educational/sacola.png', alt: 'Sacola plástica', left: '85%', top: '74%' },
  { id: 6, src: '/images/educational/lata.png', alt: 'Lata amassada', left: '60%', top: '85%' },
];
const fauna = [
  { id: 1, src: '/images/educational/garca.png', alt: 'Garça', left: '15%', top: '60%', width: 60 },
  { id: 2, src: '/images/educational/capivara.png', alt: 'Capivara', left: '80%', top: '82%', width: 70 },
  { id: 3, src: '/images/educational/caranguejo.png', alt: 'Caranguejo', left: '40%', top: '88%', width: 40 },
  { id: 4, src: '/images/educational/garcamoura.png', alt: 'Garça-moura', left: '60%', top: '65%', width: 60 },
  { id: 5, src: '/images/educational/cardume.png', alt: 'Cardume', left: '30%', top: '92%', width: 50 },
];
const maxLives = 3;

const Educational: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [trash, setTrash] = useState(trashImages);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [lives, setLives] = useState(maxLives);
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);

  const handleClean = (id: number) => {
    setTrash((prev) => prev.filter((t) => t.id !== id));
    setScore((prev) => prev + 1);
    if (trash.length === 1) {
      setFinished(true);
    }
  };

  const handleRestart = () => {
    setTrash(trashImages);
    setScore(0);
    setFinished(false);
    setLives(maxLives);
  };

  // Funções para menu horizontal
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-green-50 py-0 px-4 font-[Montserrat]">
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6 w-full">
        <div className="flex flex-wrap space-x-6 overflow-x-hidden py-3 w-full max-w-full mx-auto">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </div>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Conteúdo do jogo */}
      <h1 className="text-2xl font-bold mb-2 text-green-900 mt-8">Jogo: Limpeza dos Manguezais</h1>
      <p className="mb-4 text-green-800">Clique nos lixos para limpar o manguezal!</p>
      <div className="relative w-[600px] h-[400px] rounded-lg border-2 border-green-700 mb-4 overflow-hidden" style={{ backgroundImage: `url(${backgroundImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        {/* Lixos */}
        {trash.map((t) => (
          <img
            key={t.id}
            src={t.src}
            alt={t.alt}
            className="absolute cursor-pointer w-16 h-16 hover:scale-110 transition-transform drop-shadow-lg"
            style={{ left: t.left, top: t.top }}
            onClick={() => handleClean(t.id)}
            title="Clique para limpar"
          />
        ))}
        {/* Fauna (animais decorativos e clicáveis) */}
        {fauna.map((animal) => (
          <img
            key={animal.id}
            src={animal.src}
            alt={animal.alt}
            className="absolute select-none cursor-pointer hover:scale-110 transition-transform"
            style={{ left: animal.left, top: animal.top, width: animal.width }}
            draggable={false}
            onClick={() => {
              if (!finished && lives > 0) {
                if (lives > 1) {
                  setLives(lives - 1);
                } else {
                  setLives(0);
                  setFinished(true);
                }
              }
            }}
            title="Não clique nos animais!"
          />
        ))}
        {/* Corações (vidas) */}
        <div className="absolute right-4 top-4 flex flex-col gap-2 z-10">
          {[...Array(maxLives)].map((_, i) => (
            <span key={i} className="text-3xl">
              {i < lives ? '💚' : '🤍'}
            </span>
          ))}
        </div>
        {/* Tela de fim de jogo */}
        {finished && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80">
            <span className="text-4xl mb-2">{lives > 0 ? '🎉' : '😢'}</span>
            <span className="font-bold text-green-800 text-xl">
              {lives > 0 ? 'Parabéns! Você limpou o manguezal!' : 'Você perdeu todas as vidas!'}
            </span>
            <button
              className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              onClick={handleRestart}
            >
              Jogar Novamente
            </button>
          </div>
        )}
      </div>
      <div className="text-lg font-semibold text-green-900">
        Pontuação: {score} / {trashImages.length}
      </div>
      <div className="mt-2 text-sm text-gray-500">Dica: Você pode trocar as imagens em <code>/public/images/educational/</code></div>
    </div>
  );
};

export default Educational;